// src/components/admin/MandatosTmpPanel.jsx
import { useEffect, useState, useRef } from "react";
import Button from "./Button";
import TextInput from "./TextInput";
import { API_MAND, API, fetchJson } from "../Utiles/api";
import {
  Loader2,
  Edit3,
  CheckCircle2,
  Trash2,
  Save,
  RefreshCw,
  Download,
  X,
} from "lucide-react";
import { exportRowsToExcel } from "../Utiles/excel";
import { toIntOrNull } from "../Utiles/helpers";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const isDev =
  typeof process !== "undefined" && process.env.NODE_ENV !== "production";

const toStr = (v) => String(v ?? "").trim();
const nifLower = (v) => toStr(v).toLowerCase(); // 👈 usar SIEMPRE minúsculas
const nombreCompleto = (c) =>
  [c?.nombre, c?.apellido_1, c?.apellido_2].filter(Boolean).join(" ").trim();

// Si API es objeto, saca una base URL válida
const pickBaseUrl = (maybeAPI) => {
  if (typeof maybeAPI === "string") return maybeAPI.replace(/\/$/, "");
  return (
    maybeAPI?.BASE_URL?.replace?.(/\/$/, "") ||
    maybeAPI?.baseUrl?.replace?.(/\/$/, "") ||
    maybeAPI?.root?.replace?.(/\/$/, "") ||
    (typeof window !== "undefined" ? window.location.origin : "")
  );
};

const MandatosTmpPanel = ({ toast, setToast }) => {
  const { usuario, token: ctxToken } = useAuth();
  const authToken = ctxToken ?? usuario?.token ?? null; // ✅ JWT real
  const API_BASE = process.env.REACT_APP_API_URL;
  const [rows, setRows] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cache: nif(minúsculas) -> datos colegiado
  const colegiadoByNifCache = useRef(new Map());

  // --- Llamada EXACTA: GET ${API_BASE}/api/admin/buscar?nif=<minúsculas> ---
  const fetchColegiadoPorNif = async (nifRaw) => {
    const nif = nifLower(nifRaw);
    if (!nif) return null;

    if (colegiadoByNifCache.current.has(nif)) {
      return colegiadoByNifCache.current.get(nif);
    }

    const url = `${API_BASE}/api/admin/buscar?nif=${encodeURIComponent(nif)}`; // 👈 query directa
    try {
      if (isDev) console.debug("[buscar colegiado] →", { url, hasToken: !!authToken });
      const { data } = await axios.get(url, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });

      const first = data?.content?.[0] ?? null;
      const c = first
        ? {
          nombre: first.nombre ?? "",
          apellido_1: first.apellido_1 ?? "",
          apellido_2: first.apellido_2 ?? "",
          codigo: first.codigo ?? null,
          nif: first.nif ?? nif,
        }
        : null;

      colegiadoByNifCache.current.set(nif, c);
      return c;
    } catch (err) {
      if (isDev) {
        console.warn("[buscar colegiado][ERROR]", {
          url,
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
      }
      colegiadoByNifCache.current.set(nif, null);
      return null;
    }
  };

  // ----- carga tabla -----
  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchJson(API_MAND.tmpList);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast(e.message || "Error listando mandatos temporales");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  // Prefetch por NIF
  useEffect(() => {
    const run = async () => {
      const seen = new Set();
      for (const r of rows) {
        const nif = nifLower(r.nifNorm ?? r.nif_norm ?? r.nif);
        if (nif && !seen.has(nif) && !colegiadoByNifCache.current.has(nif)) {
          seen.add(nif);
          await fetchColegiadoPorNif(nif);
        }
      }
    };
    run();
  }, [rows, authToken, API_BASE]);

  // ---- edición ----
  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      colegiadoCodigo: r.colegiadoCodigo ?? r.colegiado_codigo ?? null,
      nifNorm: r.nifNorm ?? r.nif_norm ?? r.nif ?? "",
      ibanNorm: r.ibanNorm ?? r.iban_norm ?? "",
      bic: r.bic ?? "",
      fechaFirma: (r.fechaFirma ?? r.fecha_firma ?? "")?.slice?.(0, 10) || "",
      mndtId: r.mndtId ?? r.mndt_id ?? "",
      activo: r.activo ?? true,
    });
  };
  const cancelEdit = () => {
    setEditId(null);
    setForm({});
  };

  const saveEdit = async (id) => {
    try {
      setBusyId(id);
      const body = { ...form };
      body.colegiadoCodigo = toIntOrNull(body.colegiadoCodigo);
      if (!toStr(body.fechaFirma)) body.fechaFirma = null;
      ["nifNorm", "ibanNorm", "bic", "mndtId"].forEach((k) => {
        if (typeof body[k] === "string" && !toStr(body[k])) body[k] = null;
      });
      await fetchJson(API_MAND.tmpUpdate(id), {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setToast(`Temporal #${id} actualizado`);
      setEditId(null);
      await load();
    } catch (e) {
      setToast(e.message || "Error actualizando temporal");
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (id) => {
    if (!confirm(`Aprobar temporal #${id} y mover a mandatos_sepa?`)) return;
    try {
      setBusyId(id);
      await fetchJson(API_MAND.tmpApprove(id), { method: "POST" });
      setToast(`Temporal #${id} aprobado → creado mandato definitivo`);
      await load();
    } catch (e) {
      setToast(e.message || "Error aprobando");
    } finally {
      setBusyId(null);
    }
  };

  // ---- Celda Colegiado ----
  // ---- Celda Colegiado ----
  const ColegiadoCell = ({ nif, editing, bindNif }) => {
    const [info, setInfo] = useState(null); // guarda codigo + nombre

    useEffect(() => {
      let alive = true;
      const work = async () => {
        const key = nifLower(nif); // 👈 minúsculas
        if (!key) {
          if (alive) setInfo(null);
          return;
        }
        const cached = colegiadoByNifCache.current.get(key);
        if (cached !== undefined) {
          if (alive) setInfo(cached);
          return;
        }
        const det = await fetchColegiadoPorNif(key);
        if (!alive) return;
        setInfo(det);
      };
      work();
      return () => {
        alive = false;
      };
    }, [nif, authToken, API_BASE]);

    const display =
      info && info.codigo
        ? `${info.codigo} · ${nombreCompleto(info)}`
        : info
          ? nombreCompleto(info)
          : "";

    if (editing) {
      return (
        <div className="text-left">
          <input
            type="text"
            {...bindNif}
            className="w-full border rounded px-2 py-1"
          />
          <div className="text-xs text-gray-500 mt-1">
            {nifLower(nif) ? display || "Buscando…" : "—"}
          </div>
        </div>
      );
    }

    return (
      <div className="text-left">
        <div>{display || "—"}</div>
      </div>
    );
  };


  const Row = ({ r }) => {
    const editing = editId === r.id;
    const disabled = busyId === r.id;

    const bind = (k) => ({
      value:
        form[k] ??
        r[k] ??
        r[k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)] ??
        "",
      onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
      disabled: !editing || disabled,
      className: "w-full border rounded px-2 py-1",
    });

    const nifValue = editing
      ? form.nifNorm
      : r.nifNorm ?? r.nif_norm ?? r.nif ?? "";

    return (
      <tr className="border-t">
        <td>
          <ColegiadoCell
            nif={nifValue}
            editing={editing}
            bindNif={bind("nifNorm")}
          />
        </td>
        <td><input type="text" {...bind("ibanNorm")} /></td>
        <td><input type="text" {...bind("bic")} /></td>
        <td><input type="date" {...bind("fechaFirma")} /></td>
        <td className="font-mono text-xs">{r.mndtId ?? r.mndt_id ?? "—"}</td>
        <td className="flex gap-2">
          {!editing ? (
            <>
              <Button variant="ghost" iconLeft={<Edit3 size={16} />} onClick={() => startEdit(r)}>Editar</Button>
              <Button variant="subtle" iconLeft={disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} onClick={() => approve(r.id)} disabled={disabled}>Aprobar</Button>
              <Button variant="danger" iconLeft={<Trash2 size={16} />} onClick={() => {
                if (confirm("Eliminar?")) fetchJson(API_MAND.tmpDelete(r.id), { method: "DELETE" }).then(load);
              }}>Eliminar</Button>
            </>
          ) : (
            <>
              <Button variant="primary" iconLeft={disabled ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} onClick={() => saveEdit(r.id)} disabled={disabled}>Guardar</Button>
              <Button variant="outline" iconLeft={<X size={16} />} onClick={cancelEdit}>Cancelar</Button>
            </>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4">
      <div className="flex justify-between mb-3">
        <h2 className="text-lg font-semibold">Mandatos SEPA Temporales</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            iconLeft={<Download size={16} />}
            onClick={() => {
              exportRowsToExcel({
                filename: "mandatos_temporales.xlsx",
                rows,
                columns: [
                  { header: "ID", value: (r) => r.id },
                  {
                    header: "Colegiado",
                    value: (r) => {
                      const nif = nifLower(r.nifNorm ?? r.nif_norm ?? r.nif);
                      const det = colegiadoByNifCache.current.get(nif);
                      return det ? nombreCompleto(det) : "";
                    },
                  },
                  { header: "NIF", value: (r) => (r.nifNorm ?? r.nif_norm ?? r.nif ?? "").toLowerCase() },
                  { header: "IBAN", value: (r) => r.ibanNorm ?? r.iban_norm ?? "" },
                  { header: "BIC", value: (r) => r.bic ?? "" },
                  {
                    header: "Fecha firma",
                    value: (r) =>
                      (r.fechaFirma ?? r.fecha_firma ?? "").slice?.(0, 10) ||
                      "",
                  },
                  { header: "mndt_id", value: (r) => r.mndtId ?? r.mndt_id ?? "" },
                  {
                    header: "Activo",
                    value: (r) =>
                      r.activo === true || String(r.activo) === "true" ? "Sí" : "No",
                  },
                ],
              });
            }}
          >
            Exportar
          </Button>
          <Button variant="ghost" iconLeft={<RefreshCw size={16} />} onClick={load}>
            Refrescar
          </Button>
        </div>
      </div>

      {!authToken && (
        <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Aviso: no hay JWT en <code>usuario.token</code>. La búsqueda requerirá Authorization Bearer.
        </div>
      )}

      <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th>Colegiado</th>
              <th>IBAN</th>
              <th>BIC</th>
              <th>Fecha</th>
              <th>mndt_id</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-400">
                  Sin temporales
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <Row key={r.id} r={r} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MandatosTmpPanel;
